import i18n from 'i18next';
import { isPhoneNumberValid } from 'utils';
import { getResponseJson } from 'actions/helpers';
import fetchApi from 'utils/fetch';
import { STATUS_SUCCESS, STATUS_FAILURE } from 'types/app';

export const validateEmpty = (message = 'Required') => {
  return {
    required: {
      value: true,
      message,
    },
  };
};

export const validateEmailAddress = (message = 'Invalid Email') => ({
  pattern: {
    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
    message,
  },
});

export const validateWhitespace = (message = 'Required') => {
  return {
    pattern: {
      value:
        /^(?!-|')[0-9a-zA-Zà-ÿÀ-Ÿ\s\-'.,]{1,100}\s+(?!-|')[0-9a-zA-Zà-ÿÀ-Ÿ\s\-'.,]{1,100}$/i,
      message,
    },
  };
};

export const validateName = (message = 'Invalid name') => ({
  pattern: {
    value: /^(?!-|')[0-9a-zA-Zà-ÿÀ-Ÿ\s\-'.,]{1,100}$/i,
    message,
  },
});

export const validatePhoneNumber = (message = 'invalidPhoneNumber') => ({
  validate: (value: string) => {
    if (!value) return true;
    return isPhoneNumberValid(value) || (i18n.t(message) as string);
  },
});

export const validateIsEmptyAndPhoneNumber = (
  isPhoneRequired: boolean,
  telephoneCountryCode: string,
  message = 'invalidPhoneNumber',
) => ({
  validate: (value: string) => {
    if (!isPhoneRequired && !value) return true;

    const valueWithoutSpaces = value.replace(/\s/g, '') ?? '';
    const escapedCode = telephoneCountryCode.replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&',
    );
    const regex = new RegExp(`^\\s*${escapedCode}\\s*.*\\d.*`);

    if (isPhoneRequired && !regex.test(valueWithoutSpaces))
      return i18n.t('requiredPhoneNumber') as string;

    const digitsInValue = (value.replace(/\D/g, '') ?? '').length;
    const digitsInCountryCode = (telephoneCountryCode.replace(/\D/g, '') ?? '')
      .length;

    const maxCountryCodeDigits = digitsInCountryCode || 3;
    if (digitsInValue <= maxCountryCodeDigits) {
      return true;
    }

    return isPhoneNumberValid(value) || (i18n.t(message) as string);
  },
});

export const validatePhoneRequired = (message = 'Required') => ({
  validate: (value: string) => {
    if (!value || !isPhoneNumberValid(value)) return message;
    return true;
  },
});

const formatResponse = (status: string, message: string) => {
  return {
    status,
    message,
  };
};

export const validateAsyncPhoneNumber = async (value: string) => {
  // This is not being used anywhere at the moment
  try {
    await fetchApi.post(`v1/phone-number/validate`, {
      json: { phoneNumber: value },
    });
    return formatResponse(STATUS_SUCCESS, 'Success');
  } catch (e) {
    const error = getResponseJson(e);
    if (error && error.status === 422) {
      const invalidPhoneError =
        error?.validationErrors?.phoneNumber?.isMobilePhone;
      if (invalidPhoneError) {
        return formatResponse(STATUS_FAILURE, invalidPhoneError);
      }
    }
    return formatResponse(
      STATUS_FAILURE,
      'Oops, we are having trouble validating. We are working on fixing it',
    );
  }
};
